import {
  ArrowRightLeft, Pencil, Power, Trash2, Upload,
} from "lucide-react";
import { useMemo, useState } from "react";
import { ActionMenu } from "@/shared/components/ActionMenu";
import { DeleteConfirmationDialog } from "@/shared/components/DeleteConfirmationDialog";
import { TanStackTable } from "@/shared/components/TanStackTable";
import type { TanStackColumn } from "@/shared/components/TanStackTable";
import { Badge } from "@/shared/ui/Badge";
import { Button } from "@/shared/ui/Button";
import { Input } from "@/shared/ui/Input";
import { ACCOUNTING_TABLE_CLASSNAME } from "../../configs/tableStyles";
import {
  useAccountingBudgetsQuery,
  useAccountingSettingsQuery,
  useDeleteAccountingBudgetMutation,
  useUpsertAccountingBudgetMutation,
} from "../../hooks/useAccountingQueries";
import { fromCents, parseAmount, toCents } from "../../utils/budgetInput";
import { refundDefaultActions } from "../../utils/refundDefault";
import CategoryMergeDialog from "../budget/CategoryMergeDialog";
import InactiveCategories from "../budget/InactiveCategories";
import RefundDefaultBadge from "../budget/RefundDefaultBadge";
import RenameCategoryDialog from "../budget/RenameCategoryDialog";
import { AccountNumberCell, BudgetAmountCell, COMPACT_COLUMN_CLASS } from "../budget/categoryTable.cells";
import { useCategoryBulkActions } from "../budget/useCategoryBulkActions";
import ImportCategoriesDrawer from "./ImportCategoriesDrawer";
import { normalizeSearchText } from "./utils";
import type { LedgerAllocationOperationType } from "../../types/accounting.types";

/**
 * ADM-621 — gestion des catégories comptables dans le drawer d'un mouvement.
 * Iso avec le tableau de la page Budgets : mêmes actions via l'ActionMenu
 * partagé, renommage et fusion par les mêmes dialogues, mêmes colonnes
 * « Compte comptable » et « Budget » (celle-ci sur l'exercice courant, la même
 * jauge par type qu'en page Budgets). Les mutations de catégories restent
 * injectées par l'appelant (contexte mouvement ou org) ; les budgets, eux,
 * sont toujours org-scoped, le panel les gère en direct.
 */
export type CategoryManagerPanelProps = {
  /** Paramètres : catalogue seul, sans montants ni requêtes de budget. */
  showBudgets?: boolean
  /** Sans mouvement, l’import utilise la route de l’organisation. */
  bankMovementId?: string
  selectedSimpleBudgetType: "ASC" | "AEP"
  categoryManagerFilter: string
  setCategoryManagerFilter: (value: string) => void
  operationTypes: LedgerAllocationOperationType[]
  inactiveOperationTypes: LedgerAllocationOperationType[]
  isLoadingOperationTypes: boolean
  isLoadingInactiveOperationTypes: boolean
  isSimpleEditorBusy: boolean
  onDisableOperationType: (operationTypeId: string) => void
  onEnableOperationType: (operationTypeId: string) => void
  onRenameOperationType: (operationTypeId: string, name: string) => void
  onMergeOperationType: (sourceId: string, targetId: string) => void
  onDeleteOperationType: (operationTypeId: string) => void
  onUpdateAccountNumber: (operationTypeId: string, numeroCompte: string | null) => void
  /** YTI-2011 : sans lui (drawer d'un mouvement), l'action n'est pas proposée. */
  onSetRefundDefault?: (category: LedgerAllocationOperationType, defaultFor: "refund" | null) => void
  /** Drawer d'affectation : oublie la catégorie du brouillon en cours après une action groupée. */
  onOperationTypeRemoved?: (operationTypeId: string) => void
};

const EMPTY_SORTS: never[] = [];

export const CategoryManagerImportButton = ({ onClick }: { onClick: () => void }) => (
  <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={onClick}>
    <Upload className="h-3.5 w-3.5 mr-1.5" />
    Importer XLSX
  </Button>
);

const CategoryManagerPanel = ({
  bankMovementId,
  showBudgets = true,
  selectedSimpleBudgetType,
  categoryManagerFilter,
  setCategoryManagerFilter,
  operationTypes,
  inactiveOperationTypes,
  isLoadingOperationTypes,
  isLoadingInactiveOperationTypes: _isLoadingInactiveOperationTypes,
  isSimpleEditorBusy,
  onDisableOperationType,
  onEnableOperationType,
  onRenameOperationType,
  onMergeOperationType,
  onDeleteOperationType,
  onUpdateAccountNumber,
  onSetRefundDefault,
  onOperationTypeRemoved,
  importOpen,
  setImportOpen,
}: CategoryManagerPanelProps & { importOpen: boolean, setImportOpen: (open: boolean) => void }) => {
  const [pendingDelete, setPendingDelete] = useState<LedgerAllocationOperationType | null>(null);
  const [renameTarget, setRenameTarget] = useState<LedgerAllocationOperationType | null>(null);
  const [mergeSource, setMergeSource] = useState<LedgerAllocationOperationType | null>(null);

  // Budgets de l'exercice courant, mêmes règles qu'en page Budgets : brouillon
  // + OK, jauge du type vérifiée à la frappe en centimes, 0 = retrait.
  const { data: settings } = useAccountingSettingsQuery(showBudgets);
  const exercice = settings?.currentExercice ?? null;
  const { data: budgetsData } = useAccountingBudgetsQuery(
    { exercice: exercice ?? undefined },
    showBudgets && exercice !== null,
  );
  const upsertBudget = useUpsertAccountingBudgetMutation();
  const deleteBudget = useDeleteAccountingBudgetMutation();

  const envelopeAmount = budgetsData?.envelopes[selectedSimpleBudgetType]?.montant ?? null;
  const allocated = budgetsData?.allocated[selectedSimpleBudgetType] ?? 0;
  const budgetByCategoryId = useMemo(() => {
    const map = new Map<number, { id: number, montant: number }>();
    (budgetsData?.categoryBudgets ?? []).forEach((budget) => {
      if (budget.operationTypeId !== null) {
        map.set(budget.operationTypeId, { id: budget.id, montant: budget.montant });
      }
    });
    return map;
  }, [budgetsData]);

  const maxAssignableTo = (savedAmount: number) => {
    if (envelopeAmount === null) return null;
    return fromCents(toCents(envelopeAmount) - toCents(allocated) + toCents(savedAmount));
  };

  const saveBudget = async (operationType: LedgerAllocationOperationType, raw: string) => {
    if (exercice === null) return;

    const amount = parseAmount(raw);
    if (amount === null) return;

    const existing = budgetByCategoryId.get(Number(operationType.id));
    const savedAmount = existing?.montant ?? 0;
    if (amount === savedAmount) return;

    try {
      if (amount === 0 && existing) {
        await deleteBudget.mutateAsync(existing.id);
      }
      else {
        await upsertBudget.mutateAsync({ exercice, operationTypeId: Number(operationType.id), montant: amount });
      }
    }
    catch {
      // Les mutations budgets toastent déjà l'erreur de jauge côté API.
    }
  };

  const mergeCandidates = useMemo(
    () => [...operationTypes, ...inactiveOperationTypes],
    [operationTypes, inactiveOperationTypes],
  );

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    onDeleteOperationType(pendingDelete.id);
    setPendingDelete(null);
  };

  const saveAccountNumber = (operationType: LedgerAllocationOperationType, numeroCompte: string | null) => {
    if ((numeroCompte ?? "") === (operationType.numero_compte ?? "")) return;
    // Vide = effacé : le compte comptable est facultatif.
    onUpdateAccountNumber(operationType.id, numeroCompte);
  };

  const pendingCount = pendingDelete?.ledger_allocations_count ?? 0;

  const filterList = (list: LedgerAllocationOperationType[]) => {
    const query = normalizeSearchText(categoryManagerFilter);
    if (!query) return list;
    return list.filter((operationType) => normalizeSearchText(operationType.name).includes(query));
  };

  const filteredActiveOperationTypes = useMemo(
    () => filterList(operationTypes),

    [categoryManagerFilter, operationTypes],
  );
  const filteredInactiveOperationTypes = useMemo(
    () => filterList(inactiveOperationTypes),

    [categoryManagerFilter, inactiveOperationTypes],
  );

  const categoryBulk = useCategoryBulkActions(
    filteredActiveOperationTypes,
    `${selectedSimpleBudgetType}:${categoryManagerFilter}`,
    { onRemoved: onOperationTypeRemoved },
  );

  const buildColumns = (): TanStackColumn<LedgerAllocationOperationType>[] => [
    {
      id: "name",
      accessorKey: "name",
      label: "Catégorie",
      header: "Catégorie",
      enableSorting: false,
      canWidthGrow: true,
      columnClassName: COMPACT_COLUMN_CLASS,
      minSize: 160,
      cell: ({ row }) => (
        <div className="space-y-0.5">
          <span className="block font-medium text-slate-800">
            {row.original.name}
            {!showBudgets && <Badge variant="outline" className="ml-2">{row.original.nature === "revenue" ? "Recette" : "Dépense"}</Badge>}
            {row.original.imported_from_pcg && (
              <Badge variant="outline" className="ml-1.5 h-4 px-1 text-[9px] font-medium" title="Créée par l'import du plan comptable général">
                PCG
              </Badge>
            )}
            {(row.original.ledger_allocations_count ?? 0) > 0 && (
              <span className="ml-2 text-[10px] font-normal text-slate-500">
                {`(${row.original.ledger_allocations_count} affectation${(row.original.ledger_allocations_count ?? 0) > 1 ? "s" : ""})`}
              </span>
            )}
          </span>
          {/* Sous le nom, comme en page Budgets : en ligne, un nom long le masquait. */}
          <RefundDefaultBadge category={row.original} />
        </div>
      ),
    },
    {
      id: "numero_compte",
      label: "Compte comptable",
      // « Compte comptable » et non « numéro de compte » : ce dernier
      // évoquerait un compte bancaire.
      header: () => <span title="N° du plan comptable général, facultatif">Compte comptable</span>,
      enableSorting: false,
      disableTruncate: true,
      columnClassName: COMPACT_COLUMN_CLASS,
      size: 170,
      cell: ({ row }) => (
        <AccountNumberCell category={row.original} isBusy={isSimpleEditorBusy} onSave={saveAccountNumber} />
      ),
    },
    {
      id: "budget",
      label: "Budget",
      header: () => <span title={`Budget ${selectedSimpleBudgetType} de l'exercice en cours`}>Budget</span>,
      enableSorting: false,
      disableTruncate: true,
      columnClassName: COMPACT_COLUMN_CLASS,
      size: 210,
      cell: ({ row }) => (
        <BudgetAmountCell
          category={row.original}
          savedAmount={budgetByCategoryId.get(Number(row.original.id))?.montant ?? 0}
          maxAssignable={maxAssignableTo(budgetByCategoryId.get(Number(row.original.id))?.montant ?? 0)}
          isBusy={false}
          onSave={saveBudget}
        />
      ),
    },
    {
      id: "actions",
      label: "Actions",
      header: () => null,
      enableSorting: false,
      disableTruncate: true,
      columnClassName: COMPACT_COLUMN_CLASS,
      size: 120,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <ActionMenu
            className="h-7 px-2 text-xs"
            actions={[
              { label: "Renommer", icon: Pencil, onClick: () => setRenameTarget(row.original) },
              { label: "Désactiver", icon: Power, onClick: () => onDisableOperationType(row.original.id) },
              { label: "Fusionner", icon: ArrowRightLeft, onClick: () => setMergeSource(row.original) },
              ...(onSetRefundDefault ? refundDefaultActions(row.original, onSetRefundDefault) : []),
              // `ActionMenu` détecte « Supprimer » et la place après un
              // séparateur, en destructif.
              { label: "Supprimer", icon: Trash2, onClick: () => setPendingDelete(row.original) },
            ]}
          />
        </div>
      ),
    },
  ];

  // Colonnes stables par section : reconstruites à la frappe, flexRender
  // remonterait les cellules et les inputs perdraient le focus.

  const activeColumns = useMemo(() => buildColumns().filter((column) => showBudgets || column.id !== "budget"), [budgetByCategoryId, envelopeAmount, allocated, isSimpleEditorBusy, selectedSimpleBudgetType, showBudgets, onSetRefundDefault]);
  const activeColumnsWithSelection = useMemo(
    () => [categoryBulk.selectionColumn, ...activeColumns],
    [categoryBulk.selectionColumn, activeColumns],
  );

  // Agencement iso page Budgets : titre de section, puis la barre d'outils du
  // tableau (pill de comptage + sélecteur de colonnes), puis le tableau.
  const renderActiveTable = (rows: LedgerAllocationOperationType[]) => (
    <div className="space-y-1">
      <h3 className="text-sm font-medium">Catégories actives</h3>
      <TanStackTable
        data={rows}
        columns={activeColumnsWithSelection}
        isLoading={isLoadingOperationTypes}
        skeletonRowCount={4}
        counterType="catégorie"
        metadata={{
          total_items: rows.length,
          total_count: rows.length,
          total_pages: 1,
          current_page: 1,
          per_page: rows.length,
        }}
        storageKey={showBudgets ? "accounting-catmgr-active-table-v1" : "accounting-settings-categories-table-v1"}
        activeSorts={EMPTY_SORTS}
        enableSorting={false}
        getRowId={(operationType) => operationType.id}
        className={ACCOUNTING_TABLE_CLASSNAME}
        getRowClassName={() => "text-[12px] leading-tight"}
      />
    </div>
  );

  return (
    <>
      <div className="space-y-3">
        <Input
          value={categoryManagerFilter}
          onChange={(event) => setCategoryManagerFilter(event.target.value)}
          placeholder="Filtrer par nom de catégorie"
          className="h-8 text-xs"
        />
        {categoryBulk.toolbar}
        {renderActiveTable(filteredActiveOperationTypes)}
        {/* Repliées comme sur la page Budgets : nombreuses et sans autre geste
            utile que la réactivation. */}
        {filteredInactiveOperationTypes.length > 0 && (
          <InactiveCategories
            categories={filteredInactiveOperationTypes}
            onEnable={onEnableOperationType}
          />
        )}
      </div>

      <DeleteConfirmationDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        isLoading={isSimpleEditorBusy}
        itemName={pendingDelete?.name ?? ""}
        title="Supprimer cette catégorie ?"
        description={pendingCount > 0
          ? `La catégorie "${pendingDelete?.name}" est utilisée par ${pendingCount} affectation${pendingCount > 1 ? "s" : ""} du grand livre. Après suppression, ${pendingCount > 1 ? "ces affectations resteront" : "cette affectation restera"} en place mais ${pendingCount > 1 ? "seront décatégorisées" : "sera décatégorisée"} (apparaîtra en "Non catégorisé" dans la synthèse). Cette action est irréversible.`
          : `La catégorie "${pendingDelete?.name}" n'est liée à aucune affectation. La suppression est sans impact sur le grand livre. Cette action est irréversible.`}
        confirmLabel={pendingCount > 0 ? `Supprimer et décatégoriser ${pendingCount} affectation${pendingCount > 1 ? "s" : ""}` : "Supprimer"}
      />

      <RenameCategoryDialog
        category={renameTarget}
        isBusy={isSimpleEditorBusy}
        onClose={() => setRenameTarget(null)}
        onConfirm={(name) => {
          if (renameTarget) onRenameOperationType(renameTarget.id, name);
          setRenameTarget(null);
        }}
      />

      <CategoryMergeDialog
        source={mergeSource}
        candidates={mergeCandidates.filter((candidate) => candidate.nature === mergeSource?.nature)}
        budgetType={selectedSimpleBudgetType}
        isBusy={isSimpleEditorBusy}
        onClose={() => setMergeSource(null)}
        onConfirm={(targetId) => {
          if (mergeSource) onMergeOperationType(mergeSource.id, targetId);
          setMergeSource(null);
        }}
      />

      <ImportCategoriesDrawer
        open={importOpen}
        onOpenChange={setImportOpen}
        bankMovementId={bankMovementId}
      />
    </>
  );
};

export default CategoryManagerPanel;
